/**
 * Visualização Detalhada de Configuração de Chamada de Entrada
 * VigosCloud - Incoming Call System
 *
 * Padrão visual: CallcenterV2 (ccv2-*)
 */

import React, { useState, useEffect } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import {
  accessLocalStorage,
  getIncomingConfigById,
  deleteIncomingConfig
} from '../../../services';

import Loading from '../../Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faEdit,
  faTrash,
  faPhone,
  faServer,
  faExchangeAlt,
  faCalendarAlt,
  faRecordVinyl,
  faVolumeUp,
  faCheck,
  faTimes
} from '@fortawesome/free-solid-svg-icons';

import './IncomingCalls.css';
import '../../../pages/CallcenterV2/config-styles.css';

function IncomingCallsView() {
  const history = useHistory();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  useEffect(() => {
    loadConfig();
  }, [id]);

  const loadConfig = async () => {
    try {
      setLoading(true);

      const user = await accessLocalStorage.getUserLocalStorage();
      if (!user?.user?.idtenant) {
        history.push('/');
        return;
      }

      const response = await getIncomingConfigById(id);

      if (response.success) {
        setConfig(response.data);
      } else {
        alert('Configuração não encontrada');
        history.push('/config/chamadas-entrada');
      }

    } catch (error) {
      console.error('[INCOMING-VIEW] Erro ao carregar configuração:', error);
      alert('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await deleteIncomingConfig(id);

      if (response.success) {
        alert('Configuração excluída com sucesso!');
        history.push('/config/chamadas-entrada');
      } else {
        alert(`Erro ao excluir: ${response.error}`);
      }

    } catch (error) {
      console.error('[INCOMING-VIEW] Erro ao excluir:', error);
      alert('Erro ao excluir configuração');
    }
  };

  const getDestinationTypeLabel = (type) => {
    const types = {
      endpoint: 'Ramal',
      queue: 'Fila',
      external: 'Externo',
      disa: 'DISA/URA',
      whatsapp: 'WhatsApp',
      hangup: 'Desligar'
    };
    return types[type] || type;
  };

  if (loading) {
    return <Loading />;
  }

  if (!config) {
    return (
      <div className="incoming-page">
        <div className="incoming-content">
          <div className="ccv2-config-card" style={{ padding: '2rem', textAlign: 'center', color: '#dc2626' }}>
            Configuração não encontrada
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="incoming-page">
      <div className="incoming-content">

        {/* Header */}
        <div className="incoming-header">
          <div className="incoming-title-wrapper">
            <FontAwesomeIcon icon={faPhone} className="incoming-icon" />
            <div>
              <h1 className="incoming-title">Visualizar Chamada de Entrada</h1>
              <p className="incoming-subtitle">Detalhes completos da configuração</p>
            </div>
          </div>
          <div className="incoming-actions">
            <Link to="/config/chamadas-entrada" className="ccv2-btn ccv2-btn-sm ccv2-btn-outline" style={{ textDecoration: 'none', color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}>
              <FontAwesomeIcon icon={faArrowLeft} /> Voltar
            </Link>
            <Link to={`/config/chamadas-entrada/${id}/editar`} className="ccv2-btn ccv2-btn-sm" style={{ textDecoration: 'none', background: '#f59e0b', color: '#fff' }}>
              <FontAwesomeIcon icon={faEdit} /> Editar
            </Link>
            <button className="ccv2-btn ccv2-btn-sm" style={{ background: '#dc2626', color: '#fff' }} onClick={() => setDeleteModal(true)}>
              <FontAwesomeIcon icon={faTrash} /> Excluir
            </button>
          </div>
        </div>

        {/* Status Badge */}
        <div>
          {config.active ? (
            <span className="ccv2-badge ccv2-badge--success" style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}>
              <FontAwesomeIcon icon={faCheck} /> Ativo
            </span>
          ) : (
            <span className="ccv2-badge ccv2-badge--danger" style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}>
              <FontAwesomeIcon icon={faTimes} /> Inativo
            </span>
          )}
        </div>

        {/* Informações Básicas */}
        <div className="ccv2-config-card" style={{ padding: '1.5rem' }}>
          <h3 className="incoming-section-title">Informações Básicas</h3>
          <div className="incoming-info-grid">
            <div className="incoming-info-item">
              <span className="incoming-info-label">Máscara DID</span>
              <span className="incoming-info-value" style={{ fontSize: '1.25rem' }}>{config.mask}</span>
            </div>
            <div className="incoming-info-item">
              <span className="incoming-info-label">Tronco</span>
              <span className="incoming-info-value">
                {config.trunk_name ? (
                  <span className="ccv2-badge ccv2-badge--info">
                    <FontAwesomeIcon icon={faServer} /> {config.trunk_name}
                  </span>
                ) : (
                  <span style={{ color: '#94a3b8' }}>Nenhum</span>
                )}
              </span>
            </div>
            <div className="incoming-info-item">
              <span className="incoming-info-label">Tenant</span>
              <span className="incoming-info-value">{config.tenant}</span>
            </div>
          </div>
          {config.description && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
              <span className="incoming-info-label">Descrição</span>
              <p style={{ color: '#334155', marginTop: '0.25rem' }}>{config.description}</p>
            </div>
          )}
        </div>

        {/* Destino Principal */}
        <div className="ccv2-config-card" style={{ padding: '1.5rem' }}>
          <h3 className="incoming-section-title">
            <FontAwesomeIcon icon={faPhone} /> Destino Principal
          </h3>
          <div className="incoming-info-grid">
            <div className="incoming-info-item">
              <span className="incoming-info-label">Tipo de Destino</span>
              <span className="incoming-info-value">
                <span className="ccv2-badge ccv2-badge--info">{getDestinationTypeLabel(config.destination_type_main)}</span>
              </span>
            </div>
            {config.destination_main && (
              <div className="incoming-info-item">
                <span className="incoming-info-label">Destino</span>
                <span className="incoming-info-value">{config.destination_main}</span>
              </div>
            )}
          </div>
        </div>

        {/* Recursos Habilitados */}
        <div className="ccv2-config-card" style={{ padding: '1.5rem' }}>
          <h3 className="incoming-section-title">Recursos Habilitados</h3>
          <div className="incoming-resources-grid">
            <div className={`incoming-resource-card ${config.recording_enabled ? 'enabled' : 'disabled'}`}>
              <FontAwesomeIcon icon={faRecordVinyl} className="incoming-resource-icon" />
              <div className="incoming-resource-text">
                <div className="incoming-resource-title">Gravação</div>
                <div className="incoming-resource-value">
                  {config.recording_enabled ? `Habilitada (${config.recording_format?.toUpperCase() || 'WAV'})` : 'Desabilitada'}
                </div>
              </div>
            </div>
            <div className={`incoming-resource-card ${config.audio_enabled ? 'info' : 'disabled'}`}>
              <FontAwesomeIcon icon={faVolumeUp} className="incoming-resource-icon" />
              <div className="incoming-resource-text">
                <div className="incoming-resource-title">Áudio</div>
                <div className="incoming-resource-value">
                  {config.audio_enabled ? (config.audio_file || 'Habilitado') : 'Desabilitado'}
                </div>
              </div>
            </div>
            <div className={`incoming-resource-card ${parseInt(config.overflow_count) > 0 ? 'warning' : 'disabled'}`}>
              <FontAwesomeIcon icon={faExchangeAlt} className="incoming-resource-icon" />
              <div className="incoming-resource-text">
                <div className="incoming-resource-title">Transbordos</div>
                <div className="incoming-resource-value">
                  {parseInt(config.overflow_count) || 0} configurado(s)
                </div>
              </div>
            </div>
            <div className={`incoming-resource-card ${parseInt(config.gotoiftime_count) > 0 ? 'info' : 'disabled'}`}>
              <FontAwesomeIcon icon={faCalendarAlt} className="incoming-resource-icon" />
              <div className="incoming-resource-text">
                <div className="incoming-resource-title">Time-Conditions</div>
                <div className="incoming-resource-value">
                  {parseInt(config.gotoiftime_count) || 0} regra(s)
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Overflows Detalhados */}
        {config.overflows && config.overflows.length > 0 && (
          <div className="ccv2-config-card" style={{ padding: '1.5rem' }}>
            <h3 className="incoming-section-title">
              <FontAwesomeIcon icon={faExchangeAlt} /> Transbordos Configurados
            </h3>
            {config.overflows.map((overflow, index) => (
              <div key={index} className="incoming-detail-card">
                <div className="incoming-detail-header">
                  <span className="ccv2-badge ccv2-badge--warn">Transbordo {index + 1}</span>
                  {overflow.active ? (
                    <span className="ccv2-badge ccv2-badge--success">Ativo</span>
                  ) : (
                    <span className="ccv2-badge">Inativo</span>
                  )}
                </div>
                <div className="incoming-detail-grid">
                  <div className="incoming-detail-item">
                    <span className="incoming-detail-label">Timeout</span>
                    <span className="incoming-detail-value">{overflow.timeout_seconds}s</span>
                  </div>
                  <div className="incoming-detail-item">
                    <span className="incoming-detail-label">Tipo</span>
                    <span className="incoming-detail-value">{getDestinationTypeLabel(overflow.destination_type)}</span>
                  </div>
                  <div className="incoming-detail-item">
                    <span className="incoming-detail-label">Destino</span>
                    <span className="incoming-detail-value">{overflow.destination_overflow || '-'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Time-Conditions Detalhadas */}
        {config.gotoiftime_rules && config.gotoiftime_rules.length > 0 && (
          <div className="ccv2-config-card" style={{ padding: '1.5rem' }}>
            <h3 className="incoming-section-title">
              <FontAwesomeIcon icon={faCalendarAlt} /> Condições de Tempo
            </h3>
            {config.gotoiftime_rules.map((rule, index) => (
              <div key={index} className="incoming-detail-card">
                <div className="incoming-detail-header">
                  <span className="ccv2-badge ccv2-badge--info">Regra {index + 1}</span>
                  {rule.active ? (
                    <span className="ccv2-badge ccv2-badge--success">Ativa</span>
                  ) : (
                    <span className="ccv2-badge">Inativa</span>
                  )}
                </div>
                {rule.description && (
                  <p style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '0.75rem' }}>
                    <strong>Descrição:</strong> {rule.description}
                  </p>
                )}
                <div className="incoming-detail-grid">
                  <div className="incoming-detail-item">
                    <span className="incoming-detail-label">Horário</span>
                    <span className="incoming-detail-value">{rule.time_range}</span>
                  </div>
                  <div className="incoming-detail-item">
                    <span className="incoming-detail-label">Dias da Semana</span>
                    <span className="incoming-detail-value">{rule.days_of_week}</span>
                  </div>
                  <div className="incoming-detail-item">
                    <span className="incoming-detail-label">Dias do Mês</span>
                    <span className="incoming-detail-value">{rule.days_of_month}</span>
                  </div>
                  <div className="incoming-detail-item">
                    <span className="incoming-detail-label">Meses</span>
                    <span className="incoming-detail-value">{rule.months}</span>
                  </div>
                </div>
                <div className="incoming-code-box">
                  {rule.context_name},{rule.extension_name},{rule.priority}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Metadados */}
        <div className="incoming-metadata">
          <h3 className="incoming-section-title" style={{ fontSize: '0.8125rem' }}>Metadados</h3>
          <div className="incoming-metadata-grid">
            <div className="incoming-info-item">
              <span className="incoming-info-label">ID</span>
              <span className="incoming-detail-value">{config.id}</span>
            </div>
            <div className="incoming-info-item">
              <span className="incoming-info-label">Criado em</span>
              <span className="incoming-detail-value">{new Date(config.created_at).toLocaleString('pt-BR')}</span>
            </div>
            {config.updated_at && (
              <div className="incoming-info-item">
                <span className="incoming-info-label">Atualizado em</span>
                <span className="incoming-detail-value">{new Date(config.updated_at).toLocaleString('pt-BR')}</span>
              </div>
            )}
            {config.created_by && (
              <div className="incoming-info-item">
                <span className="incoming-info-label">Criado por</span>
                <span className="incoming-detail-value">{config.created_by}</span>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Modal de Confirmação de Exclusão */}
      {deleteModal && (
        <div className="ccv2-modal-overlay" onClick={() => setDeleteModal(false)}>
          <div className="ccv2-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ccv2-modal-header" style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}>
              <h2>
                <FontAwesomeIcon icon={faTrash} /> Confirmar Exclusão
              </h2>
              <button className="ccv2-modal-close" onClick={() => setDeleteModal(false)}>
                &times;
              </button>
            </div>
            <div className="ccv2-modal-body">
              <p style={{ marginBottom: '1rem' }}>
                Tem certeza que deseja excluir a configuração para a máscara:
              </p>
              <p style={{ textAlign: 'center', margin: '1.5rem 0' }}>
                <strong style={{ fontSize: '1.25rem', color: '#dc2626' }}>{config.mask}</strong>
              </p>
              <div className="incoming-warning-box">
                Esta ação não pode ser desfeita.
              </div>
            </div>
            <div className="ccv2-modal-footer">
              <button className="ccv2-btn ccv2-btn-secondary" onClick={() => setDeleteModal(false)}>
                Cancelar
              </button>
              <button
                className="ccv2-btn"
                style={{ background: '#dc2626', color: '#fff' }}
                onClick={handleDelete}
              >
                <FontAwesomeIcon icon={faTrash} /> Confirmar Exclusão
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default IncomingCallsView;
